import React, { useState, useEffect } from 'react';
import { UserService, UserCreateData } from '../../services/UserService';
import { CampaignService } from '../../services/CampaignService';
import { UserRole } from '../../types/enums';
import { normalizePhone } from '../../utils/phone';

interface UserCreationModalProps { open: boolean; onClose: () => void; onUserCreated: () => void; }

const inputClass = "w-full px-3 py-2.5 border border-[#d1d5db] rounded-lg text-sm focus:outline-none focus:border-[#2563eb] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] transition-all disabled:bg-[#f3f4f6] disabled:cursor-not-allowed";
const glowBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease', minWidth: '130px' };
const cancelBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #f2f6ff, #e4ebff)', color: '#0c1a36', border: '1px solid #dfe7ff', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer', minWidth: '130px' };

const UserCreationModal: React.FC<UserCreationModalProps> = ({ open, onClose, onUserCreated }) => {
  const [formData, setFormData] = useState<UserCreateData>({ username: '', full_name: '', email: '', password: '', phone: '', role: UserRole.USER_CLIENT, campaign_ids: [] });
  const [campaigns, setCampaigns] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [campaignError, setCampaignError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setFormData({ username: '', full_name: '', email: '', password: '', phone: '', role: UserRole.USER_CLIENT, campaign_ids: [] }); setError(null); setPhoneError(null); setCampaignError(null); setLoading(false); }
  }, [open]);

  useEffect(() => {
    if (open) CampaignService.list().then(setCampaigns).catch(() => setCampaignError("Não foi possível carregar campanhas."));
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') setPhoneError(null);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleCampaign = (id: number) => setFormData(prev => {
    const exists = prev.campaign_ids?.includes(id);
    return { ...prev, campaign_ids: exists ? (prev.campaign_ids || []).filter(c => c !== id) : [...(prev.campaign_ids || []), id] };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); e.stopPropagation();
    setError(null); setPhoneError(null);
    const { normalized, error: validationError } = normalizePhone(formData.phone);
    if (validationError) { setPhoneError(validationError); return; }
    if (!formData.full_name.trim()) { setError('Informe o nome do usuário.'); return; }
    setLoading(true);
    try {
      await UserService.createUser({ ...formData, full_name: formData.full_name.trim(), phone: normalized ?? undefined, campaign_ids: formData.campaign_ids?.length ? Array.from(new Set(formData.campaign_ids)) : undefined });
      onUserCreated(); onClose();
    } catch { setError('Falha ao criar usuário. Verifique os dados e tente novamente.'); }
    finally { setLoading(false); }
  };

  if (!open) return null;

  const allowedRoles = [UserRole.ADMIN, UserRole.USER_HUB, UserRole.USER_CLIENT_ADMIN, UserRole.USER_CLIENT];
  const labelClass = "block text-sm font-semibold text-[#374151] mb-1";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="bg-white rounded-xl w-[90%] max-w-xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h2 className="m-0 text-xl font-bold text-[#111827]">Criar Novo Usuário</h2>
          <button className="bg-transparent border-none text-2xl cursor-pointer text-[#718096] hover:text-[#2d3748] transition-colors" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {[
            { id: 'uc-username', label: 'Nome de Usuário', name: 'username', type: 'text', required: true },
            { id: 'uc-full_name', label: 'Nome', name: 'full_name', type: 'text', required: true },
            { id: 'uc-email', label: 'Email', name: 'email', type: 'email', required: true },
            { id: 'uc-password', label: 'Senha', name: 'password', type: 'password', required: true },
          ].map(({ id, label, name, type, required }) => (
            <div key={id}>
              <label className={labelClass} htmlFor={id}>{label}</label>
              <input id={id} type={type} name={name} value={(formData as any)[name] || ''} onChange={handleChange} required={required} disabled={loading} className={inputClass} />
            </div>
          ))}

          <div>
            <label className={labelClass} htmlFor="uc-phone">Celular (opcional)</label>
            <input id="uc-phone" type="tel" name="phone" placeholder="(11) 98888-7777" value={formData.phone || ''} onChange={handleChange} disabled={loading} className={inputClass} />
            {phoneError && <p className="text-red-600 text-xs mt-1">{phoneError}</p>}
          </div>

          <div>
            <label className={labelClass} htmlFor="uc-role">Cargo</label>
            <select id="uc-role" name="role" value={formData.role} onChange={handleChange} required disabled={loading} className={inputClass}>
              {allowedRoles.map(role => <option key={role} value={role}>{(role.charAt(0) + role.slice(1).toLowerCase()).replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          <div>
            <label className={labelClass}>Campanhas</label>
            {campaignError && <p className="text-amber-600 text-xs mt-1">{campaignError}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {campaigns.map(c => {
                const checked = formData.campaign_ids?.includes(c.id) || false;
                return (
                  <label key={c.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-all ${checked ? 'bg-blue-50 border-blue-400 text-blue-700 font-semibold' : 'bg-[#f8fafc] border-[#e5e7eb] text-[#374151] hover:bg-[#f0f4ff] hover:border-blue-300'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCampaign(c.id)} disabled={loading} className="sr-only" />
                    <span>{c.name}</span>
                  </label>
                );
              })}
              {campaigns.length === 0 && !campaignError && <span className="col-span-full text-[#9ca3af] text-sm">Nenhuma campanha cadastrada.</span>}
            </div>
          </div>

          {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded">{error}</p>}

          <div className="flex justify-end gap-3 mt-2">
            <button type="button" style={cancelBtn} onClick={onClose} disabled={loading}>Cancelar</button>
            <button type="submit" style={glowBtn} disabled={loading} className="disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none">{loading ? 'Criando...' : 'Criar Usuário'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreationModal;
